/**
 * Factory global + `createLogger(scope)` — entry point usado em todo código.
 *
 * **Lifecycle:**
 * 1. Boot do app (composition root) chama `setGlobalLoggerFactory(pinoFactory)`
 *    — substituindo o NOOP default.
 * 2. Demais módulos podem chamar `createLogger('scope')` no top-level, antes do
 *    boot. O logger troca o delegate NOOP pelo factory instalado posteriormente.
 * 3. Testes podem injetar factory mock via `setGlobalLoggerFactory(...)` e
 *    restaurar o NOOP no `afterEach` via `setGlobalLoggerFactory(noopLoggerFactory)`.
 *
 * **Por que não passa factory via DI explícita?** Logger é cross-cutting concern.
 * DI explícita poluiria assinaturas de funções triviais. Padrão validado em
 * VS Code, pino, log4j — global mutável **só** em composition root.
 */
import { noopLoggerFactory } from './noop.js';
import type { Logger, LoggerFactory } from './types.js';

let globalFactory: LoggerFactory = noopLoggerFactory;
let globalFactoryRevision = 0;

/**
 * Inicializa o factory global. Chamada **uma vez** no boot da app
 * (composition root) — nunca dentro de packages downstream.
 *
 * Em testes, restaurar o NOOP no `afterEach` para isolamento entre cases.
 */
export function setGlobalLoggerFactory(factory: LoggerFactory): void {
  globalFactory = factory;
  globalFactoryRevision += 1;
}

class LateBoundLogger implements Logger {
  private delegate: Logger;
  private delegateRevision: number;

  constructor(
    private readonly scope: string,
    private readonly defaults: Record<string, unknown> | undefined,
    private readonly childScopes: readonly Record<string, unknown>[] = [],
    initialDelegate?: Logger,
  ) {
    this.delegateRevision = globalFactoryRevision;
    this.delegate = initialDelegate ?? this.createDelegate();
  }

  debug(msg: string, context?: Record<string, unknown>): void {
    const delegate = this.resolveDelegate();
    context === undefined ? delegate.debug(msg) : delegate.debug(msg, context);
  }

  info(msg: string, context?: Record<string, unknown>): void {
    const delegate = this.resolveDelegate();
    context === undefined ? delegate.info(msg) : delegate.info(msg, context);
  }

  warn(msg: string, context?: Record<string, unknown>): void {
    const delegate = this.resolveDelegate();
    context === undefined ? delegate.warn(msg) : delegate.warn(msg, context);
  }

  error(msg: string, context?: Record<string, unknown>): void {
    const delegate = this.resolveDelegate();
    context === undefined ? delegate.error(msg) : delegate.error(msg, context);
  }

  fatal(msg: string, context?: Record<string, unknown>): void {
    const delegate = this.resolveDelegate();
    context === undefined ? delegate.fatal(msg) : delegate.fatal(msg, context);
  }

  child(childScope: Record<string, unknown>): Logger {
    const childDelegate = this.resolveDelegate().child(childScope);
    return new LateBoundLogger(
      this.scope,
      this.defaults,
      [...this.childScopes, childScope],
      childDelegate,
    );
  }

  private resolveDelegate(): Logger {
    if (this.delegateRevision !== globalFactoryRevision) {
      this.delegate = this.createDelegate();
      this.delegateRevision = globalFactoryRevision;
    }
    return this.delegate;
  }

  private createDelegate(): Logger {
    let delegate = globalFactory.create(this.scope, this.defaults);
    for (const childScope of this.childScopes) {
      delegate = delegate.child(childScope);
    }
    return delegate;
  }
}

/**
 * Cria logger com scope. Use em todo lugar.
 *
 * @example
 *   const log = createLogger('pack-runtime');
 *   log.info('pack.load.started', { packId });
 *
 *   // Com defaults aplicados a todos os logs:
 *   const reqLog = createLogger('http', { requestId: ctx.id });
 *   reqLog.info('inbound', { path });  // log inclui requestId automático
 */
export function createLogger(scope: string, defaults?: Record<string, unknown>): Logger {
  return new LateBoundLogger(scope, defaults);
}
